import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
} from '../../modules/mockDataCommon'
import { getContactListDataByPersonId } from '../contacts/mockData'
import {
  getTeamMemberListData,
  getTeamMemberListDataByPersonId,
} from '../teamMembers/mockData'
import { getUserListData, getUserListDataByPersonId } from '../users/mockData'
import { requireAuthenticatedUser } from '../../decorators'
import { getPersonListData, getPersonDataById } from './mockData'
import { getCompanyContactListData } from '../companyContacts/mockData'
import { getSupplierContactListData } from '../supplierContacts/mockData'
import { getTsdContactListData } from '../tsdContacts/mockData'
import { getLeadSourceContactListData } from '../selectLists/leadSourceContacts/mockData'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import { getNextVersionNumberString } from '../../modules/getNextVersionNumberString'
import { isStaleVersion } from '../../modules/versionConflict'
import type { IPerson } from '~/types/person/models'

interface GetPersonsResponse {
  data: IPerson[]
}

async function getPersons(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const responseBody: GetPersonsResponse = { data: await getPersonListData() }
  return res(ctx.status(200), ctx.json(responseBody))
}

/**
 * ⚠ THE PLAIN DETAIL GET WAS NEVER MOCKED. This module registered `/api/persons`,
 * `/api/persons/match`, `:id/contacts`, `:id/teammembers`, `:id/users`, POST,
 * PATCH `:id` and DELETE `:id` — everything EXCEPT `GET /api/persons/:id`. So the
 * request fell through MSW to the dev proxy, 404'd, and `httpGet`'s `default:`
 * branch threw. The one caller is `linkExistingPersonToParent`
 * (cynqueModules/personMatch.ts), which is how clicking a duplicate-person
 * candidate silently did nothing: the sub-dialog never closed and the parent's
 * Person field was never filled.
 *
 * The real API implements this route, so the feature worked on staging the whole
 * time — the same mock-narrower-than-the-server shape as the missing `/match`
 * route and the unreachable tier C. `getPersonDataById` already existed in
 * ./mockData; only the registration was missing.
 */
async function getPerson(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const data = await getPersonDataById(id)
  return res(ctx.status(200), ctx.json({ data }))
}

async function getContactsByPersonId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning contacts for person "${id}".`)
  return res(
    ctx.status(200),
    ctx.json({ data: await getContactListDataByPersonId(id) })
  )
}

async function getTeamMembersByPersonId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning teamMembers for person "${id}".`)
  return res(
    ctx.status(200),
    ctx.json({ data: await getTeamMemberListDataByPersonId(id) })
  )
}

async function getUsersByPersonId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning users for person "${id}".`)
  return res(
    ctx.status(200),
    ctx.json({ data: await getUserListDataByPersonId(id) })
  )
}

// post

const postPerson = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(req, res, ctx, getPersonListData)
}

const patchPerson = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getPersonListData)
}

/**
 * POST /api/persons/:id/merge -- fold `loserId` INTO the path person (the
 * survivor). Mirrors cynque-api-server #1539 (resources/api/persons), in its
 * order:
 *  - 400 a malformed body, or a self-merge;
 *  - 404 either person missing;
 *  - 409 "Person version conflict" when the survivor's version is stale;
 *  - re-points the loser's company / supplier / TSD / lead-source contacts,
 *    users and team members to the survivor (the server's _REPOINT_TABLES);
 *  - fills the survivor's EMPTY email, phone, cell and address from the loser
 *    (survivorship -- a value the survivor already has is kept);
 *  - deletes the loser, and returns the survivor (200).
 *
 * ⚠ NARROWER than the server in one way: it writes no dedup-decision record
 * (#1556). Nothing in the webapp reads one.
 *
 * It did not exist until 2026-10-09: before that a merge under MSW fell through
 * (`onUnhandledRequest: 'bypass'`) to the dev proxy, which has no API base URL
 * locally and answers 503 -- so a person merge could never succeed on :3000.
 */
const mergePerson = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const fn = '[api/msw/api/persons/index.ts] mergePerson |'
  const { id } = req.params
  const body = (await req.json()) as { loserId?: string; version?: string }
  const problem = (status: number, title: string, detail: string) =>
    res(ctx.status(status), ctx.json({ status, title, detail }))
  if (typeof id !== 'string' || !body?.loserId || !body.version) {
    return problem(
      400,
      'Validation failed',
      'loserId and version are required.'
    )
  }
  if (body.loserId === id) {
    return problem(
      400,
      'Cannot merge a person into itself',
      `loser_id and the path id are both ${id}.`
    )
  }
  const persons = await getPersonListData()
  const survivor = persons.find((p) => p.id === id)
  if (!survivor) {
    return problem(404, 'Survivor person not found', `No person with id ${id}.`)
  }
  const loser = persons.find((p) => p.id === body.loserId)
  if (!loser) {
    return problem(
      404,
      'Loser person not found',
      `No person with id ${body.loserId}.`
    )
  }
  if (isStaleVersion(body.version, survivor.version)) {
    return problem(
      409,
      'Person version conflict',
      'The survivor person was modified concurrently; re-read and retry.'
    )
  }

  // Survivorship: the survivor keeps what it has; only its empty fields fill.
  for (const key of ['email', 'phone', 'cell'] as const) {
    if (!survivor[key] && loser[key]) {
      survivor[key] = loser[key]
    }
  }
  if (!survivor.address && loser.address) {
    survivor.address = loser.address
  }
  survivor.version = getNextVersionNumberString(survivor.version)

  // Re-point every reference, then persist each list that changed.
  const survivorRef = { ...survivor }
  const repoint = async (
    list: Array<{ person?: { id?: string } | null }>,
    entityKeyBase: string
  ) => {
    let moved = 0
    for (const row of list) {
      if (row.person?.id === loser.id) {
        row.person = { ...survivorRef }
        moved++
      }
    }
    if (moved) {
      await _storeEntityDataListToLocalForage({
        entityKeyBase,
        listData: list as never,
        callingFn: fn,
      })
    }
  }
  await repoint(await getCompanyContactListData(), 'companycontacts')
  await repoint(await getSupplierContactListData(), 'suppliercontacts')
  await repoint(await getTsdContactListData(), 'tsdcontacts')
  await repoint(await getLeadSourceContactListData(), 'leadsourcecontacts')
  await repoint(await getUserListData(), 'users')
  await repoint(await getTeamMemberListData(), 'teammembers')

  persons.splice(persons.indexOf(loser), 1)
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'persons',
    listData: persons as never,
    callingFn: fn,
  })
  return res(ctx.status(200), ctx.json({ data: survivor }))
}

const deletePerson = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getPersonListData)
}

/**
 * `GET /api/persons/match` — the "did you mean…?" candidates
 * (cynque-api-server#1539).
 *
 * ⚠ THIS WAS MISSING, AND ITS ABSENCE LOGGED THE USER OUT. With no handler,
 * MSW passed the request through to the dev server's API proxy, which answers
 * 403 in mock mode — and `httpGet` treats any 403 as "session/token no longer
 * valid" (services/http/get.ts), so it notified, logged out and redirected.
 * Typing a name into a person form ended the session. The feature worked on
 * staging the whole time, because the real API implements the route; only mock
 * mode — which is how the app is developed — was broken.
 *
 * Tiering follows the contract documented on IPersonMatchCandidate, THREE tiers:
 * "A" for a shared email or an exclusively-held phone plus a corroborating name,
 * "B" for any other exact key intersection, "C" for a corroborating name alone.
 *
 * ⚠ This mock emitted only A and B until 2026-08-28, so tier C — which the live
 * server has always been able to return — was unreachable in development and in
 * every e2e test, and staging was the first place it could appear.
 *
 * ⚠ Self-exclusion is deliberately absent. The client sends only name / email /
 * phone, never the id of the record being edited, so the real server cannot
 * exclude the person you are editing either — and a mock that did would be
 * kinder than the thing it models.
 */
const _norm = (value: unknown) =>
  typeof value === 'string' ? value.trim().toLowerCase() : ''

/** Digits only, so '(777) 123-2342' and '7771232342' are the same phone. */
const _digits = (value: unknown) =>
  typeof value === 'string' ? value.replace(/\D/g, '') : ''

/** "Rob" is a prefix of "Robert". Equal strings are NOT — sharesName covers those. */
const _isPrefixOf = (short: string, long: string) =>
  !!short && !!long && short !== long && long.startsWith(short)

const matchPersons = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const params = req.url.searchParams
  const firstName = _norm(params.get('firstName'))
  const lastName = _norm(params.get('lastName'))
  const email = _norm(params.get('email'))
  const phone = _digits(params.get('phone'))
  const limit = Number(params.get('limit')) || 20

  // No identifying input, no candidates — the client guards this too, but the
  // endpoint must not depend on it.
  if (!firstName && !lastName && !email && !phone) {
    return res(ctx.status(200), ctx.json({ data: [] }))
  }

  const listData = (await getPersonListData()) as IPerson[]

  // How many people hold each number, so an EXCLUSIVELY-held phone can be told
  // from a switchboard — the distinction the server's tier A turns on. Counted
  // per person through a Set, so one human listing the same number as both phone
  // and cell does not read as two holders.
  const phoneHolderCount = new Map<string, number>()
  for (const person of listData as any[]) {
    const held = new Set(
      [_digits(person.phone), _digits(person.cell)].filter(Boolean)
    )
    for (const number of held) {
      phoneHolderCount.set(number, (phoneHolderCount.get(number) ?? 0) + 1)
    }
  }

  const candidates = listData
    .map((person: any) => {
      const personFirst = _norm(person.name?.firstName)
      const personLast = _norm(person.name?.lastName)
      const sharesEmail = !!email && _norm(person.email) === email
      const sharesPhone =
        !!phone &&
        (_digits(person.phone) === phone || _digits(person.cell) === phone)
      // Both halves when both were typed, so "Isaac Smith" does not match every
      // Isaac; whichever half was typed when only one was.
      const sharesName =
        (!!firstName || !!lastName) &&
        (!firstName || personFirst === firstName) &&
        (!lastName || personLast === lastName)
      // A number nobody else carries is as identifying as an email; one several
      // people carry is a switchboard, and is not.
      const sharesExclusivePhone =
        sharesPhone && (phoneHolderCount.get(phone) ?? 0) <= 1

      // The mock's stand-in for the server's trigram + nickname matching: an
      // exactly-equal surname carrying a given name that is a prefix of the other
      // ("Rob" -> "Robert"). Narrow next to pg_trgm, but it is precisely the case
      // #120 exists to surface, and it is what lets this mock produce a C at all.
      const similarName =
        !sharesName &&
        (!!firstName || !!lastName) &&
        (!lastName || personLast === lastName) &&
        (!firstName ||
          _isPrefixOf(firstName, personFirst) ||
          _isPrefixOf(personFirst, firstName))

      const sharesExactKey = sharesEmail || sharesPhone || sharesName

      return {
        id: person.id,
        descr: [person.name?.firstName, person.name?.lastName]
          .filter(Boolean)
          .join(' '),
        email: person.email,
        tier:
          (sharesEmail || sharesExclusivePhone) && (sharesName || similarName)
            ? 'A'
            : sharesExactKey
            ? 'B'
            : 'C',
        sharesEmail,
        sharesPhone,
        sharesName,
        // Sent because the real server sends it (`similar_name`), not because
        // anything here reads it. IPersonMatchCandidate models neither this nor
        // the five other ranking signals the server returns; the mock being the
        // WIDER of the two is the safe direction for that gap to run.
        similarName,
      }
    })
    .filter(
      (c) => c.sharesEmail || c.sharesPhone || c.sharesName || c.similarName
    )
    // Tier order is alphabetical, so A before B before C. The panel renders them
    // in the order it is given.
    .sort((a, b) => (a.tier < b.tier ? -1 : a.tier > b.tier ? 1 : 0))
    .slice(0, limit)

  return res(ctx.status(200), ctx.json({ data: candidates }))
}

export const handlers = [
  {
    method: 'get',
    path: '/api/persons',
    handler: getPersons,
  },
  {
    method: 'get',
    path: '/api/persons/match',
    handler: matchPersons,
  },
  // ⚠ ORDER IS LOAD-BEARING: '/api/persons/match' must stay ABOVE this one, or
  // ':id' swallows it and the match endpoint becomes a lookup for a person whose
  // id is the literal string "match" — reinstating the 403-logout bug in a new
  // costume. Nothing enforces this but the order of these entries.
  {
    method: 'get',
    path: '/api/persons/:id',
    handler: getPerson,
  },
  {
    method: 'get',
    path: '/api/persons/:id/contacts',
    handler: getContactsByPersonId,
  },
  {
    method: 'get',
    path: '/api/persons/:id/teammembers',
    handler: getTeamMembersByPersonId,
  },
  {
    method: 'get',
    path: '/api/persons/:id/users',
    handler: getUsersByPersonId,
  },
  {
    method: 'post',
    path: '/api/persons',
    handler: postPerson,
  },
  {
    method: 'patch',
    path: '/api/persons/:id',
    handler: patchPerson,
  },
  {
    method: 'delete',
    path: '/api/persons/:id',
    handler: deletePerson,
  },
  {
    method: 'post',
    path: '/api/persons/:id/merge',
    handler: mergePerson,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
